<script setup lang="ts">
/* eslint-disable max-lines */
import { RouterView, useRoute, useRouter, type RouteRecordNameGeneric } from 'vue-router'
import { onBeforeMount, onErrorCaptured, onMounted, ref, watch } from 'vue'
import { GlobalError } from './utils/globalError/GlobalError'
import { useMiiaFwStore } from '@/stores/miiaFwStore'
import { storeToRefs } from 'pinia'
import MiiaFwConfig from '@/configs/config'
import { useLoadingView } from './modules/miiaFw/composables/useLoadingView'
import { useUserAttributes } from './modules/miiaFw/composables/useUserAttributes'
import { isAuthenticated } from './modules/miiaFw/utils/miiaFwCognito'
import { useCommonModalStore } from './stores/commonModalStore'
import CommonHeader from './components/header/PageHeader.vue'
import { useNetworkStore } from './stores/networkStore'
import CommonModal from '@/components/common/dialog/CommonModal.vue'
import { NetworkConnectionChecker } from '@/modules/healthCheck/networkConnectionChecker'
import { getUserAttributesFromStore } from './modules/miiaFw/MiiaFwCognito'
import {
  useGovernmentSettingStore,
  type GovernmentSettingState
} from './stores/governmentSettingStore'
import ViewLoading from './modules/miiaFw/components/ViewLoading.vue'
import { useHoldTemporaryStore } from './stores/holdTemporaryStore'
import { AuthDeviceDB } from './accessors/indexedDB/authDeviceDB'
import { getDeviceInfoDaoApi } from './accessors/api/requests/disasterCommonDeviceApiGwFuncs'
import type { ArgGetDeviceInfo } from './types/accessor/api/device/deviceInfoTypes'
import { canLogin, checkAuthority } from './modules/auth/authority'
import { ERROR_CODES } from './constants/error/errorCode'
import RecertificationDialog from '@/modules/miiaFw/components/RecertificationDialog.vue'
import { ROUTER_PATHS } from './constants/router/paths'
import { useZipHandler } from './modules/zip/useZipHandler'

const router = useRouter()
const route = useRoute()

const miiaFwStore = useMiiaFwStore()
const { isAuthorized } = storeToRefs(miiaFwStore)

const { setLoadingView } = useLoadingView()
const { getAttributes } = useUserAttributes()

const commonModalStore = useCommonModalStore()
const networkStore = useNetworkStore()
const governmentSettingStore = useGovernmentSettingStore()
const holdTemporaryStore = useHoldTemporaryStore()
const zipAccessor = useZipHandler()

// アプリコンテンツ表示可能状態フラグ
const isReady = ref(false)
const view = ref<RouteRecordNameGeneric>('')

const networkConnectionChecker = NetworkConnectionChecker.instance
const authDeviceDB = AuthDeviceDB.instance

onBeforeMount(() => {
  // 未認証状態でも表示可能なメニュー項目を設定
  miiaFwStore.updateMenu(
    router.getRoutes().filter(item => item.meta.onMenu && !item.meta.requiresAuth)
  )
  if (MiiaFwConfig.config.authorization.mustAuth) {
    // 認証必須システム
    if (isAuthorized.value) {
      // 認証済
    } else {
      // 未認証
      // トークン取得処理
      setLoadingView(true)
      isAuthenticated()
        .then(() => {
          // 未認証から認証中へ変化した場合と認証中にリフレッシュした場合は必ずisAuthorizedがfalse → trueに変化し、getInfo内の処理が実行される。
          // 認証中であった場合の処理中解除はgetInfo()処理内で実施
          // リフレッシュ後に認証済であった場合に実施する処理(リフレッシュなしで認証した場合は通らない。)
        })
        .catch(() => {
          if (route.meta.requiresAuth && router.currentRoute.value.path !== ROUTER_PATHS.login) {
            router.push({ path: ROUTER_PATHS.login }) // 先にトップ画面に戻さないと表示されてしまう。
          }
          setLoadingView(false) // 同期処理終了後に必ず実行
          isReady.value = true
        })
    }
  }
  // 現在の画面を設定
  view.value = route.name
})

onErrorCaptured(err => {
  if (err instanceof GlobalError) {
    err.showErrorMessage()
  }
  // コンソールには表示させておく
  console.error(err)
})

onMounted(() => {
  // ネットワークに接続可能かどうかのチェッカー起動
  networkConnectionChecker.clearCheckingConnection()
  networkConnectionChecker.startCheckingConnection()
  // 残りの同期処理のエラーをキャッチ
  window.addEventListener('error', event => {
    if (event.error instanceof GlobalError) {
      event.error.showErrorMessage()
    }
    console.error(event.error)
  })
  // 残りの非同期処理のエラーをキャッチ
  window.addEventListener('unhandledrejection', event => {
    if (event.reason instanceof GlobalError) {
      // イベントを抑止する
      event.preventDefault()
      event.reason.showErrorMessage()
    }
    console.error(event.reason)
  })
  setLoadingView(false)
})

/**
 * デバイス認証API実行
 * @param deviceInfoGetParams デバイス認証API用パラメータ
 * @return なし
 */
const getDeviceInfo = async (deviceInfoGetParams: ArgGetDeviceInfo) => {
  return await getDeviceInfoDaoApi(deviceInfoGetParams).catch(err => {
    console.error(err)
    throw new GlobalError([ERROR_CODES.LOGIN_DEVICE_GET_FAILED])
  })
}

/**
 * デバイス認証判定
 * @param govSetting
 * @returns なし
 */
const judgeAuthDevice = (govSetting: GovernmentSettingState) => {
  const isUndefinedAll = (obj: GovernmentSettingState): boolean => {
    return Object.keys(obj).every(key => obj[key as keyof GovernmentSettingState] === undefined)
  }

  // govSettingのプロパティがすべてundefinedのとき、エラーを発生させる
  if (isUndefinedAll(govSetting)) {
    holdTemporaryStore.changeDeviceAuthFlag(false)
    throw new GlobalError([ERROR_CODES.LOGIN_GOVERNMENT_SETTINGS_EMPTY])
  }
  // デバイス認証が必要かどうかチェック
  // undefinedが入ってくる可能性があり、falseかどうかきちんと判定するため===falseとする
  const skipDeviceAuth =
    govSetting.isDeviceAuthentication === false || govSetting.serviceLimitEnable
  if (skipDeviceAuth) {
    holdTemporaryStore.changeDeviceAuthFlag(true)
    return
  }
  holdTemporaryStore.changeDeviceAuthFlag(false)
}

watch(isAuthorized, newVal => {
  if (newVal === true) {
    // 未認証から認証中へ変化した場合と認証中にリフレッシュした場合
    getAttributes()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then(async (user: any) => {
        if (!(await canLogin())) {
          throw new GlobalError([ERROR_CODES.LOGIN_AUTH_GET_FAILED])
        }
        // 認可処理
        checkAuthority()
        // 認証が必要なメニューも含めて表示
        miiaFwStore.updateMenu(
          router.getRoutes().filter(value => {
            if (
              Object.prototype.hasOwnProperty.call(value.meta, 'accessibleRoles') &&
              Array.isArray(value.meta.accessibleRoles)
            ) {
              if (
                !Object.prototype.hasOwnProperty.call(user, 'attribute') ||
                !Object.prototype.hasOwnProperty.call(user.attribute, MiiaFwConfig.config.sysId)
              ) {
                return false
              }
              const userAttributes = user.attribute[MiiaFwConfig.config.sysId]
              if (Object.prototype.hasOwnProperty.call(userAttributes, 'role')) {
                return (
                  value.meta.accessibleRoles.indexOf(userAttributes.role) > -1 && value.meta.onMenu
                )
              } else {
                return false
              }
            } else {
              return value.meta.onMenu
            }
          })
        )
        miiaFwStore.updateSensitiveInfo({})
        const attribute = await getUserAttributesFromStore()
        const governmentSettingQueryParams = {
          system: MiiaFwConfig.config.sysId,
          govCode: attribute.attribute['disaster-commonattr'].govCode
        }
        await governmentSettingStore
          .getGovernmentSettings(governmentSettingQueryParams)
          .then(govSetting => {
            if (govSetting) {
              judgeAuthDevice(govSetting)
              governmentSettingStore.updateState(govSetting)
            }
          })
        // デバイス認証機能が無効であるなら、後続のデバイス認証処理は行なわない
        const isDeviceAuthentication = governmentSettingStore.isDeviceAuthentication
        if (!isDeviceAuthentication) {
          setLoadingView(false)
          return
        }
        if (await authDeviceDB.existsDeviceToken()) {
          // 【デバイス認証】デバイスAPIを叩き,デバイス情報を取得
          const deviceId = await authDeviceDB.getDeviceToken()
          if (deviceId) {
            const deviceInfoGetParams = {
              system: MiiaFwConfig.config.sysId,
              govCode: user.attribute[MiiaFwConfig.config.disasterEmergency.commonAttr].govCode,
              deviceId: deviceId
            }
            await getDeviceInfo(deviceInfoGetParams)
              .then(result => {
                if (!result || Object.keys(result).length === 0) {
                  // デバイス未登録のため登録ボタンを表示する
                  holdTemporaryStore.changeDeviceAuthFlag(false)
                } else {
                  holdTemporaryStore.changeDeviceInfo(result)
                  // デバイス登録が確認できたので、登録ボタンは表示せず画面遷移を許可する
                  holdTemporaryStore.changeDeviceAuthFlag(true)
                }
              })
              .catch(err => {
                // デバイス登録が確認できないので、登録ボタンを表示する
                holdTemporaryStore.changeDeviceAuthFlag(false)
                throw err
              })
          }
        }
        setLoadingView(false)
      })
      .catch(error => {
        setLoadingView(false)
        if (error instanceof GlobalError) {
          error.showErrorMessage()
        } else {
          commonModalStore.openAlertModal({ body: error.message })
        }
      })
  } else {
    // 未認証状態でも表示可能なメニュー項目を設定
    miiaFwStore.updateMenu(
      router.getRoutes().filter(item => item.meta.onMenu && !item.meta.requiresAuth)
    )
  }
  isReady.value = true
})

/**
 * オンラインモードへの切り替え処理実行を検知し、
 * 結果に応じてダイアログを表示する
 */
watch(
  () => networkStore.getIsTryingToSwitchOnline,
  newValue => {
    // オンライン切り替え試行中の場合は何もしない
    if (newValue) return
    if (networkStore.getIsOnline) {
      commonModalStore.openAlertModal({
        title: 'オンライン',
        body: 'ネットワークに接続できたため\nオンラインになりました。',
        okTitle: '閉じる',
        okColor: 'secondary'
      })
    } else {
      commonModalStore
        .openAlertModal({
          title: 'オフライン',
          body: 'ネットワーク接続に失敗しました。',
          okTitle: '閉じる',
          okColor: 'secondary'
        })
        .then(async () => {
          // ダウンロードすべきデータがある場合はzipダウンロード
          const shouldDownloadZip = await zipAccessor.checkShouldZipDownload()
          if (shouldDownloadZip) await zipAccessor.downloadZip()
        })
    }
  }
)

/**
 * オフラインモードへの切り替え処理実行を検知し、
 * ダイアログを表示する
 */
watch(
  () => networkStore.getIsTryingToSwitchOffline,
  newValue => {
    // オフライン切り替え試行中の場合は何もしない
    if (newValue) return
    if (!networkStore.getIsOnline) {
      commonModalStore.openAlertModal({
        title: 'オフライン',
        body: `ネットワークが切断されたためオフラインになりました。
        <br>
        <span style="color: red;">
          ※ブラウザのリロードやタブの削除を行うと調査中のデータが失われる可能性があります。
          またリロードを行うとサインアウト処理が行われるため、再度システムを使用するにはネットワークに接続してサインインを行う必要があります。
        </span>`,
        okTitle: '閉じる',
        okColor: 'secondary'
      })
    }
  }
)
</script>

<template>
  <ViewLoading v-if="miiaFwStore.isLoading" :style="{ zIndex: isReady ? undefined : 1049 }" />
  <header>
    <div v-if="isReady" class="contents">
      <CommonHeader />
    </div>
  </header>
  <CommonModal />
  <RecertificationDialog />
  <RouterView />
</template>

<style scoped>
header {
  line-height: 1.5;
  max-height: 100vh;
}

.logo {
  display: block;
  margin: 0 auto 2rem;
}

nav {
  width: 100%;
  font-size: 12px;
  text-align: center;
}

nav a.router-link-exact-active {
  color: var(--color-text);
}

nav a.router-link-exact-active:hover {
  background-color: transparent;
}

nav a {
  display: inline-block;
  padding: 0 1rem;
  border-left: 1px solid var(--color-border);
}

nav a:first-of-type {
  border: 0;
}
</style>
